/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from "react";
import { useMaterialUIController } from "context";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";

import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import Card from "@mui/material/Card";
import CardTitle from "components/CardTitle";
import MenuItem from "@mui/material/MenuItem";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import LastHourlyStateProject from "./analyticsLastState/LastHourlyStateProject";

import AnalyticsCustomSearchProject from "./analyticsCustomSearch/AnalyticsCustomSearchProject";
import LastStateIndividualApartment from "./analyticsLastState/LastStateIndividualApartment";
import AnalyticsCustomSearchToIndividualApartment from "./analyticsCustomSearch/AnalyticsCustomSearchToIndividualApartment";

const selectSx = {
  "& .MuiInputBase-root": {
    height: 44,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    padding: "10.5px 14px",
  },
};

function Analytics({
  projectId,
  individualApartmentId = null,
  individualApartmentName = null,
  project,
  projectsData,
  onnaDevicesData,
  apartmentsData,
}) {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [selectedProjectId, setSelectedProjectId] = useState("");

  const isApartmentMode = Boolean(individualApartmentId);
  const isProjectMode = Boolean(projectId) && !isApartmentMode;
  const isGlobalMode = !isProjectMode && !isApartmentMode;

  const availableProjects = useMemo(() => Object.values(projectsData || {}), [projectsData]);
  const analyticsEnabledProjectIds = useMemo(
    () =>
      new Set(
        availableProjects
          .filter((projectOption) => Boolean(projectOption?.features?.analytics))
          .map((projectOption) => projectOption?.uid)
          .filter(Boolean)
      ),
    [availableProjects]
  );

  useEffect(() => {
    if (
      selectedProjectId &&
      !Object.prototype.hasOwnProperty.call(projectsData || {}, selectedProjectId)
    ) {
      setSelectedProjectId("");
    }
  }, [selectedProjectId, projectsData]);

  useEffect(() => {
    if (selectedProjectId && !analyticsEnabledProjectIds.has(selectedProjectId)) {
      setSelectedProjectId("");
    }

    if (selectedProjectId === "" && analyticsEnabledProjectIds.size === 1) {
      setSelectedProjectId([...analyticsEnabledProjectIds][0]);
    }
  }, [selectedProjectId, analyticsEnabledProjectIds]);

  const effectiveProjectId = isGlobalMode ? selectedProjectId : projectId;

  const hasProjectProp = Object.keys(project || {}).length > 0;
  const effectiveProject =
    (hasProjectProp && project) ||
    (effectiveProjectId ? projectsData?.[effectiveProjectId] || {} : {});

  const effectiveApartments = useMemo(() => {
    if (!effectiveProjectId) return {};

    const projectApartmentIds = new Set(
      Object.keys(projectsData?.[effectiveProjectId]?.apartments || {})
    );

    return Object.entries(apartmentsData || {}).reduce((acc, [apartmentKey, apartmentValue]) => {
      const hasProjectId = apartmentValue?.projectId === effectiveProjectId;
      const isByProject = projectApartmentIds.has(apartmentValue?.apartmentId);

      if (hasProjectId || isByProject) {
        acc[apartmentKey] = apartmentValue;
      }
      return acc;
    }, {});
  }, [effectiveProjectId, apartmentsData, projectsData]);

  const effectiveOnnaDevices = useMemo(() => {
    if (!effectiveProjectId) return {};

    const onnaIdsFromApartments = new Set(
      Object.values(effectiveApartments || {})
        .map((apartmentValue) => apartmentValue?.onnaId)
        .filter(Boolean)
    );

    if (onnaIdsFromApartments.size === 0) return {};

    return [...onnaIdsFromApartments].reduce((acc, onnaUid) => {
      if (Object.prototype.hasOwnProperty.call(onnaDevicesData || {}, onnaUid)) {
        acc[onnaUid] = onnaDevicesData[onnaUid];
      }
      return acc;
    }, {});
  }, [effectiveProjectId, effectiveApartments, onnaDevicesData]);

  const deviceIds = useMemo(
    () => Object.keys(effectiveOnnaDevices || {}),
    [effectiveOnnaDevices, darkMode]
  );

  const [tab, setTab] = useState("LastHourlyState");

  // eslint-disable-next-line react/prop-types

  const analyticsContent = (
    <Card>
      <CardTitle title={t("analytics")} icon="query_stats" bgColor="info" />
      <MDBox style={{ padding: 16 }}>
        {isGlobalMode && (
          <MDBox mb={2} maxWidth={360}>
            <MDInput
              select
              fullWidth
              variant="outlined"
              value={selectedProjectId}
              onChange={(event) => setSelectedProjectId(event.target.value)}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected) return `— ${t("selectProject")} —`;
                  const foundProject = availableProjects.find((p) => p?.uid === selected);
                  return foundProject?.name || selected;
                },
                MenuProps: { disableScrollLock: true },
              }}
              sx={selectSx}
            >
              <MenuItem value="">
                <em>— {t("selectProject")} —</em>
              </MenuItem>
              {availableProjects.map((projectOption) => (
                <MenuItem
                  key={projectOption?.uid}
                  value={projectOption?.uid}
                  disabled={!projectOption?.features?.analytics}
                >
                  {projectOption?.name || projectOption?.uid}
                </MenuItem>
              ))}
            </MDInput>
          </MDBox>
        )}

        {isGlobalMode && !effectiveProjectId ? null : (
          <>
            <MDBox style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <MDButton
                onClick={() => setTab("LastHourlyState")}
                style={{
                  border:
                    tab === "LastHourlyState"
                      ? darkMode
                        ? "1px solid #e2e8f0"
                        : "1px solid #26304b"
                      : darkMode
                      ? "1px solid #0f172a"
                      : "1px solid #e5e7eb",
                  background:
                    tab === "LastHourlyState"
                      ? darkMode
                        ? "#e2e8f0"
                        : "#26304b"
                      : darkMode
                      ? "#0f172a"
                      : "#e5e7eb",
                  borderRadius: 10,
                  padding: "6px 12px",
                  fontSize: 16,
                  color:
                    tab === "LastHourlyState"
                      ? darkMode
                        ? "#0f172a"
                        : "#fff"
                      : darkMode
                      ? "#e5e7eb"
                      : "#26304b",
                }}
              >
                {individualApartmentName ? t("lastValue") : t("lastHour")}
              </MDButton>

              <MDButton
                onClick={() => setTab("AnalyticsCustomSearch")}
                style={{
                  border:
                    tab === "AnalyticsCustomSearch"
                      ? darkMode
                        ? "1px solid #e2e8f0"
                        : "1px solid #26304b"
                      : darkMode
                      ? "1px solid #0f172a"
                      : "1px solid #e5e7eb",
                  background:
                    tab === "AnalyticsCustomSearch"
                      ? darkMode
                        ? "#e2e8f0"
                        : "#26304b"
                      : darkMode
                      ? "#0f172a"
                      : "#e5e7eb",
                  borderRadius: 10,
                  padding: "6px 12px",
                  fontSize: 16,
                  color:
                    tab === "AnalyticsCustomSearch"
                      ? darkMode
                        ? "#0f172a"
                        : "#fff"
                      : darkMode
                      ? "#e5e7eb"
                      : "#26304b",
                }}
              >
                {t("customSearch")}
              </MDButton>
            </MDBox>

            <MDBox
              style={{ display: tab === "LastHourlyState" ? "block" : "none" }}
              aria-hidden={tab !== "LastHourlyState"}
            >
              {!individualApartmentId ? (
                <LastHourlyStateProject
                  projectId={effectiveProjectId}
                  deviceIds={deviceIds}
                  apartments={effectiveApartments}
                  darkMode={darkMode}
                  project={effectiveProject}
                  t={t}
                />
              ) : (
                <LastStateIndividualApartment
                  individualApartmentId={individualApartmentId}
                  individualApartmentName={individualApartmentName}
                  projectTimeZone={effectiveProject?.timeZone || "UTC"}
                  metricsConfig={effectiveProject?.metricsConfig || {}}
                  darkMode={darkMode}
                  t={t}
                />
              )}
            </MDBox>

            {tab === "AnalyticsCustomSearch" &&
              (!individualApartmentId ? (
                <AnalyticsCustomSearchProject
                  projectId={effectiveProjectId}
                  deviceIds={deviceIds}
                  apartments={effectiveApartments}
                  projectTimeZone={effectiveProject?.timeZone || "UTC"}
                  metricsConfig={effectiveProject?.metricsConfig || {}}
                  darkMode={darkMode}
                  t={t}
                />
              ) : (
                <AnalyticsCustomSearchToIndividualApartment
                  individualApartmentId={individualApartmentId}
                  individualApartmentName={individualApartmentName}
                  projectTimeZone={effectiveProject?.timeZone || "UTC"}
                  metricsConfig={effectiveProject?.metricsConfig || {}}
                  darkMode={darkMode}
                  t={t}
                />
              ))}
          </>
        )}
      </MDBox>
    </Card>
  );

  if (isGlobalMode) {
    return (
      <DashboardLayout>
        <DashboardNavbar title={t("analytics")} route="/analytics" />
        <MDBox pt={4} pb={3}>
          {analyticsContent}
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return analyticsContent;
}

Analytics.defaultProps = {
  projectId: "",
  individualApartmentId: "",
  individualApartmentName: "",
  project: {},
  projectsData: {},
  onnaDevicesData: {},
  apartmentsData: {},
};

Analytics.propTypes = {
  projectId: PropTypes.string,
  project: PropTypes.object,
  individualApartmentId: PropTypes.string,
  individualApartmentName: PropTypes.string,
  projectsData: PropTypes.object,
  onnaDevicesData: PropTypes.object,
  apartmentsData: PropTypes.object,
};

export default connect((state, ownProps) => {
  const projectsReducer = state?.projectsReducer || {};
  const onnaDevicesData = state?.onnaDevicesReducer || {};
  const apartmentsData = state?.apartmentsReducer || {};
  const isProjectManager = state?.mainUserReducer?.isProjectManager || false;
  const userProjects = state?.mainUserReducer?.projects || {};

  return {
    projectId: ownProps.projectId,
    onnaDevices: ownProps.onnaDevices,
    apartments: ownProps.apartments,
    project: ownProps.project,
    database: ownProps.database,
    projectsData: projectsReducer,
    onnaDevicesData,
    apartmentsData,
    isProjectManager,
    userProjects,
  };
})(Analytics);
